"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.parseFilter = void 0;
const parsimmon_1 = __importDefault(require("parsimmon"));
const common_1 = require("./common");
const mongoParser_1 = require("./mongoParser");
const datetimeParser_1 = require("./datetimeParser");
const binaryCondition = operator => value => ({
    conditionType: 'binary',
    operator,
    left: {
        exprType: 'placeholder',
    },
    right: {
        exprType: 'value',
        value,
    },
});
const likeCondition = (conditionType, likeString) => value => ({
    conditionType,
    left: {
        exprType: 'placeholder',
    },
    right: {
        exprType: 'value',
        value: likeString.replace('#VALUE#', value),
    },
});
const compoudCondition = conditionType => conditions => {
    if (conditions.length == 1)
        return conditions[0];
    return {
        conditionType,
        conditions,
    };
};
const unaryCondition = conditionType => () => {
    return {
        conditionType,
        expr: {
            exprType: 'placeholder',
        },
    };
};
const binaryFixedValueCondition = value => () => {
    return {
        conditionType: 'binary',
        operator: '=',
        left: {
            exprType: 'placeholder',
        },
        right: {
            exprType: 'value',
            value,
        },
    };
};
const negateCondition = condition => {
    return {
        conditionType: 'not',
        condition,
    };
};
const sqlTemplate = templateSql => {
    return {
        conditionType: 'rawTemplate',
        templateSql,
        expr: {
            exprType: 'placeholder',
        },
    };
};
const createParser = (filterType) => {
    const langDef = {
        string1: () => (0, common_1.token)(parsimmon_1.default.regexp(/"((?:\\.|.)*?)"/, 1))
            .map(common_1.interpretEscapes)
            .desc('string quoted'),
        string2: () => (0, common_1.token)(parsimmon_1.default.regexp(/'((?:\\.|.)*?)'/, 1))
            .map(common_1.interpretEscapes)
            .desc('string quoted'),
        string1Num: () => (0, common_1.token)(parsimmon_1.default.regexp(/"-?(0|[1-9][0-9]*)([.][0-9]+)?([eE][+-]?[0-9]+)?"/, 1))
            .map(Number)
            .desc('numer quoted'),
        string2Num: () => (0, common_1.token)(parsimmon_1.default.regexp(/'-?(0|[1-9][0-9]*)([.][0-9]+)?([eE][+-]?[0-9]+)?'/, 1))
            .map(Number)
            .desc('numer quoted'),
        number: () => (0, common_1.token)(parsimmon_1.default.regexp(/-?(0|[1-9][0-9]*)([.][0-9]+)?([eE][+-]?[0-9]+)?/))
            .map(Number)
            .desc('number'),
        noQuotedString: () => parsimmon_1.default.regexp(/[^\s^,^'^"]+/).desc('string unquoted'),
        sql: () => (0, common_1.token)(parsimmon_1.default.regexp(/\{(.*?)\}/, 1))
            .map(sqlTemplate)
            .desc('sql literal'),
        value: r => parsimmon_1.default.alt(...allowedValues.map(x => r[x])),
        valueTestEq: r => r.value.map(binaryCondition('=')),
        valueTestStr: r => r.value.map(likeCondition('like', '%#VALUE#%')),
        comma: () => (0, common_1.word)(','),
        not: () => (0, common_1.word)('NOT'),
        notNull: r => r.not.then(r.null).map(unaryCondition('isNotNull')),
        null: () => (0, common_1.word)('NULL').map(unaryCondition('isNull')),
        empty: () => (0, common_1.word)('EMPTY').map(unaryCondition('isEmpty')),
        notEmpty: r => r.not.then(r.empty).map(unaryCondition('isNotEmpty')),
        true: () => parsimmon_1.default.regexp(/true/i).map(binaryFixedValueCondition('1')),
        false: () => parsimmon_1.default.regexp(/false/i).map(binaryFixedValueCondition('0')),
        trueNum: () => (0, common_1.word)('1').map(binaryFixedValueCondition('1')),
        falseNum: () => (0, common_1.word)('0').map(binaryFixedValueCondition('0')),
        eq: r => (0, common_1.word)('=').then(r.value).map(binaryCondition('=')),
        ne: r => (0, common_1.word)('!=').then(r.value).map(binaryCondition('<>')),
        ne2: r => (0, common_1.word)('<>').then(r.value).map(binaryCondition('<>')),
        le: r => (0, common_1.word)('<=').then(r.value).map(binaryCondition('<=')),
        ge: r => (0, common_1.word)('>=').then(r.value).map(binaryCondition('>=')),
        lt: r => (0, common_1.word)('<').then(r.value).map(binaryCondition('<')),
        gt: r => (0, common_1.word)('>').then(r.value).map(binaryCondition('>')),
        startsWith: r => (0, common_1.word)('^').then(r.value).map(likeCondition('like', '#VALUE#%')),
        endsWith: r => (0, common_1.word)('$').then(r.value).map(likeCondition('like', '%#VALUE#')),
        contains: r => (0, common_1.word)('+').then(r.value).map(likeCondition('like', '%#VALUE#%')),
        startsWithNot: r => (0, common_1.word)('!^').then(r.value).map(likeCondition('like', '#VALUE#%')).map(negateCondition),
        endsWithNot: r => (0, common_1.word)('!$').then(r.value).map(likeCondition('like', '%#VALUE#')).map(negateCondition),
        containsNot: r => (0, common_1.word)('~').then(r.value).map(likeCondition('like', '%#VALUE#%')).map(negateCondition),
        element: r => parsimmon_1.default.alt(...allowedElements.map(x => r[x])).trim(common_1.whitespace),
        factor: r => r.element.sepBy(common_1.whitespace).map(compoudCondition('and')),
        list: r => r.factor.sepBy(r.comma).map(compoudCondition('or')),
    };
    const allowedValues = []; // 'string1', 'string2', 'number', 'noQuotedString'];
    if (filterType == 'string' || filterType == 'eval') {
        allowedValues.push('string1', 'string2', 'noQuotedString');
    }
    if (filterType == 'number') {
        allowedValues.push('string1Num', 'string2Num', 'number');
    }
    const allowedElements = ['null', 'notNull', 'eq', 'ne', 'ne2', 'sql'];
    if (filterType == 'number' || filterType == 'datetime' || filterType == 'eval') {
        allowedElements.push('le', 'ge', 'lt', 'gt');
    }
    if (filterType == 'string') {
        allowedElements.push('empty', 'notEmpty');
    }
    if (filterType == 'eval' || filterType == 'string') {
        allowedElements.push('startsWith', 'endsWith', 'contains', 'startsWithNot', 'endsWithNot', 'containsNot');
    }
    if (filterType == 'logical') {
        allowedElements.push('true', 'false', 'trueNum', 'falseNum');
    }
    if (filterType == 'eval') {
        allowedElements.push('true', 'false');
    }
    // must be last
    if (filterType == 'string' || filterType == 'eval') {
        allowedElements.push('valueTestStr');
    }
    else {
        allowedElements.push('valueTestEq');
    }
    return parsimmon_1.default.createLanguage(langDef);
};
const parsers = {
    number: createParser('number'),
    string: createParser('string'),
    logical: createParser('logical'),
    eval: createParser('eval'),
    mongo: mongoParser_1.mongoParser,
    datetime: datetimeParser_1.datetimeParser,
};
function parseFilter(value, filterType) {
    // console.log('PARSING', value, 'WITH', filterType);
    const ast = parsers[filterType].list.tryParse(value);
    // console.log('AST', ast);
    return ast;
}
exports.parseFilter = parseFilter;
